import { Module } from 'vuex';
import AnnouncementLibraryController from '@/controllers/library/announcements';
import { AnnouncementDisplayOption } from '@/interfaces/admin/announcement';
import { AnnouncementLibraryResponse } from '@/interfaces/library/announcement';

export interface ContentAnnouncementsState {
  items: AnnouncementLibraryResponse[];
  homepageAnnouncements: AnnouncementLibraryResponse[];
  libraryAnnouncements: AnnouncementLibraryResponse[];
  isLoading: boolean;
  error: string | null;
}

const state: ContentAnnouncementsState = {
  items: [],
  homepageAnnouncements: [],
  libraryAnnouncements: [],
  isLoading: false,
  error: null,
};

const mutations = {
  SET_LOADING(state: ContentAnnouncementsState, isLoading: boolean) {
    state.isLoading = isLoading;
    if (isLoading) {
      state.error = null;
    }
  },
  SET_HOMEPAGE_ANNOUNCEMENTS(state: ContentAnnouncementsState, announcements: AnnouncementLibraryResponse[]) {
    state.homepageAnnouncements = announcements;
  },
  SET_LIBRARY_ANNOUNCEMENTS(state: ContentAnnouncementsState, announcements: AnnouncementLibraryResponse[]) {
    state.libraryAnnouncements = announcements;
  },
  SET_ERROR(state: ContentAnnouncementsState, error: string | null) {
    state.error = error;
  },
};

const actions = {
  async initialize({ commit }: { commit: Function }) {
    commit('SET_LOADING', true);
    try {
      const announcements = await AnnouncementLibraryController.getActiveAnnouncements();
      const allLibraryItems: AnnouncementLibraryResponse[] = [];
      const homepageItems: AnnouncementLibraryResponse[] = [];
      announcements.forEach(announcement => {
        const displayOption = announcement.displayOption as AnnouncementDisplayOption;

        if (displayOption === 'HOMEPAGE') {
          homepageItems.push(announcement);
        } else if (displayOption === 'ALL_LIBRARY') {
          allLibraryItems.push(announcement);
          homepageItems.push(announcement);
        }
        // Announcements with other displayOptions (or null/undefined) will be ignored
      });

      commit('SET_HOMEPAGE_ANNOUNCEMENTS', homepageItems);
      commit('SET_LIBRARY_ANNOUNCEMENTS', allLibraryItems);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load library announcements';
      console.error('Error initializing library announcements:', error);
      commit('SET_ERROR', message);
      commit('SET_HOMEPAGE_ANNOUNCEMENTS', []);
      commit('SET_LIBRARY_ANNOUNCEMENTS', []);
    } finally {
      commit('SET_LOADING', false);
    }
  },
};

const getters = {
  homepageAnnouncements: (state: ContentAnnouncementsState): AnnouncementLibraryResponse[] => state.homepageAnnouncements,
  libraryAnnouncements: (state: ContentAnnouncementsState): AnnouncementLibraryResponse[] => state.libraryAnnouncements,
  isLoading: (state: ContentAnnouncementsState): boolean => state.isLoading,
  error: (state: ContentAnnouncementsState): string | null => state.error,
};

const libraryAnnouncements: Module<ContentAnnouncementsState, any> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};

export default libraryAnnouncements;
